import React from "react"
import {useTranslation} from "react-i18next"

import {Tooltip, TTooltipButtonProps} from "../../../components/Tooltip.tsx"
import {TOOLTIP_CLASS_NAME} from "./cardStyles.ts"

/**
 * A small "i" beside a title that explains the figure. The explanation opens on hover and on keyboard focus and closes
 * on Escape. It is also the button's description (`descriptionId`, an `InfoDescription` the caller places outside the
 * figure's text), so it does not depend on the tooltip being open.
 */
export const InfoTooltip: React.FC<{name: string; descriptionId: string; children: string}> = ({
  name,
  descriptionId,
  children,
}) => {
  const Button = React.useMemo(() => {
    const InfoButton = React.forwardRef<HTMLButtonElement, TTooltipButtonProps>((props, ref) => (
      <InfoTooltipButton ref={ref} {...props} name={name} descriptionId={descriptionId} />
    ))
    InfoButton.displayName = "InfoButton"
    return InfoButton
  }, [name, descriptionId])

  return (
    <Tooltip Button={Button} openOnFocus closeOnEscape>
      <div role={"tooltip"} className={TOOLTIP_CLASS_NAME}>
        {children}
      </div>
    </Tooltip>
  )
}

const InfoTooltipButton = React.forwardRef<
  HTMLButtonElement,
  TTooltipButtonProps & {name: string; descriptionId: string} & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({open: _open, close: _close, name, descriptionId, ...props}, ref) => {
  const {t} = useTranslation()

  return (
    <button
      ref={ref}
      type={"button"}
      aria-label={t("KlaraDashboard_Kpi_Info", {name})}
      aria-describedby={descriptionId}
      className={
        // The "i" is drawn by CSS, so it stays out of the surrounding text.
        "relative z-10 inline-flex size-[15px] shrink-0 cursor-help items-center justify-center rounded-full border-[1.4px] border-cr-slate-30 text-[9px] leading-none font-bold text-cr-slate-40 before:content-['i'] hover:border-cr-blue-mid hover:text-cr-blue-mid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cr-blue"
      }
      {...props}
    />
  )
})
InfoTooltipButton.displayName = "InfoTooltipButton"

/** The screen-reader copy of an explanation, referenced by its trigger's `aria-describedby`. */
export const InfoDescription: React.FC<{id: string; children: string}> = ({id, children}) => (
  <span id={id} className={"sr-only"}>
    {children}
  </span>
)
